import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import axios from "../../../api/axios";

export const EmployeeSection = ({ employeeId, title = "Employee", className = "" }) => {
  const { t } = useTranslation();
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (employeeId) {
      fetchEmployee();
    }
  }, [employeeId]);

  const fetchEmployee = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`/users/${employeeId}`);
      setEmployee(response.data);
    } catch (err) {
      console.error("Error fetching employee:", err);
      setError("Failed to load employee data");
    } finally {
      setLoading(false);
    }
  };

  if (!employeeId) {
    return null;
  }

  if (loading) {
    return (
      <div className={`shadow sm:overflow-hidden sm:rounded-md ${className}`}>
        <div className="bg-gradient-to-r from-orange-50 to-amber-50 px-6 py-4 border-b border-gray-200">
          <div className="flex items-center">
            <div className="animate-pulse flex space-x-4 w-full">
              <div className="rounded-full bg-orange-200 h-10 w-10"></div>
              <div className="flex-1 space-y-2 py-1">
                <div className="h-4 bg-orange-200 rounded w-1/4"></div>
                <div className="h-3 bg-orange-200 rounded w-1/2"></div>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white px-6 py-6">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            <div className="h-4 bg-gray-200 rounded w-2/3"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`shadow sm:overflow-hidden sm:rounded-md ${className}`}>
        <div className="bg-red-50 px-6 py-4 border-b border-red-200">
          <div className="flex items-center">
            <div className="text-red-600">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-semibold text-red-900">Error Loading Employee</h3>
              <p className="text-sm text-red-600">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!employee) {
    return null;
  }

  return (
    <div className={`shadow sm:overflow-hidden sm:rounded-md ${className}`}>
      <div className="bg-gradient-to-r from-orange-50 to-amber-50 px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <div className="flex items-center space-x-3">
                <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
                {employee.active !== undefined && (
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    employee.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {employee.active ? 'Active' : 'Inactive'}
                  </span>
                )}
                {employee.syncStatus !== undefined && (
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    employee.syncStatus ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {employee.syncStatus ? 'Synced' : 'Not Synced'}
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-600">{t("employee.contact.information")}</p>
            </div>
          </div>
          <div className="flex space-x-2">
            {employee.work_mobile && (
              <a 
                href={`tel:${employee.work_mobile}`}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                {t("Call")}
              </a>
            )}
            {(employee.email || employee.user?.email) && (
              <a 
                href={`mailto:${employee.email || employee.user?.email}`}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                {t("Email")}
              </a>
            )}
            <Link 
              to={`/dashboard/users/${employeeId}`}
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
              {t("employee.view.profile")}
            </Link>
          </div>
        </div>
      </div>
      <div className="bg-white px-6 py-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <dt className="text-sm font-medium text-gray-500">{t("Name")}</dt>
            <dd className="mt-1 text-sm text-gray-900">{employee.displayTitle || employee.name || "-"}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">{t("Email")}</dt>
            <dd className="mt-1 text-sm text-gray-900">{employee.email || employee.user?.email || "-"}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">{t("employee.work.mobile")}</dt>
            <dd className="mt-1 text-sm text-gray-900">{employee.work_mobile || "-"}</dd>
          </div>
        </div>
      </div>
    </div>
  );
};